/**
 * Decision 0166 §3.5: Settings › Subscriptions. Every subscription in the workspace, Platform Runner subscriptions and
 * the CLI logins on paired machines alike, for every member: who owns it, its name, provider and plan, account, where it
 * is shared and when it last took a run. Never a credential. §3.6: and which of them each person's own work uses in each
 * project (their choice).
 *
 * Decision 0167: by account. A subscription is an account (owner, provider, opaque account key); the Platform Runner
 * subscription and the machines' logins that carry it are its connections. Each account shows its plan limits, this
 * month's usage and one sharing setting. Account › My subscriptions shows the viewer's own accounts across workspaces.
 * The raw account key never leaves the cloud: an account is named by `acct:<ref>` (core `accountChoiceKey`).
 */
import { Schema } from "effect"
import { ProfileScope, QuotaWindow } from "./runner"

/** One login on a paired machine as the Workspace DO reports it (the Platform Runner's come from its own DO). */
export const LocalSubscription = Schema.Struct({
  runnerId: Schema.String,
  runnerName: Schema.String,
  profileId: Schema.String,
  agent: Schema.String,
  ownerUserId: Schema.String,
  plan: Schema.NullOr(Schema.String),
  /** The account as the runner reports it (masked by the runner until its probes send the full email, 0166 §3.4). */
  accountLabel: Schema.NullOr(Schema.String),
  label: Schema.NullOr(Schema.String),
  scope: ProfileScope,
  projectIds: Schema.Array(Schema.String),
  /** False: no explicit choice yet (the machine's own login: Team on a machine shared with the team, else Personal). */
  explicit: Schema.Boolean,
  lastUsedAt: Schema.NullOr(Schema.Number),
  /** Decision 0167: the login's opaque account key (its probe's `quotaAccountKey`); internal, never sent to a browser. */
  accountKey: Schema.optionalKey(Schema.String),
  /** Decision 0167 §2: its plan limits as last observed, and when. */
  quota: Schema.optionalKey(Schema.Array(QuotaWindow)),
  quotaObservedAt: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  /** Decision 0167 §2: the machine is connected now; when it was last seen. */
  online: Schema.optionalKey(Schema.Boolean),
  lastSeenAt: Schema.optionalKey(Schema.NullOr(Schema.Number)),
})
export type LocalSubscription = typeof LocalSubscription.Type

/**
 * Decision 0167 §4: one account's sharing as the Workspace DO stores it (per owner and account key). `note`: the
 * connections whose sharing differed when the first read merged them, for the owner's card. Internal.
 */
export const AccountSharingRow = Schema.Struct({
  ownerUserId: Schema.String,
  accountKey: Schema.String,
  scope: ProfileScope,
  projectIds: Schema.Array(Schema.String),
  note: Schema.NullOr(Schema.Struct({ connections: Schema.Array(Schema.String), at: Schema.Number })),
})
export type AccountSharingRow = typeof AccountSharingRow.Type

export const LocalSubscriptionsView = Schema.Struct({
  logins: Schema.Array(LocalSubscription),
  /** When each Platform Runner profile last took a run, by profile id. */
  platformUsed: Schema.Record(Schema.String, Schema.Number),
  /** Decision 0167 §4: every stored account sharing of the workspace (absent from a Workspace DO before 0167). */
  accounts: Schema.optionalKey(Schema.Array(AccountSharingRow)),
  /** Decision 0167 §2: the Platform Runner is connected to the hub now; when it was last seen. */
  platform: Schema.optionalKey(
    Schema.Struct({ online: Schema.Boolean, lastSeenAt: Schema.NullOr(Schema.Number) }),
  ),
  /** Decision 0167 §2: the Platform Runner profiles' limits as the hub last saw them, by profile. */
  platformLimits: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        profileId: Schema.String,
        subscriptionId: Schema.NullOr(Schema.String),
        quota: Schema.Array(QuotaWindow),
        quotaObservedAt: Schema.NullOr(Schema.Number),
      }),
    ),
  ),
})
export type LocalSubscriptionsView = typeof LocalSubscriptionsView.Type

/** Decision 0167: one connection of an account: a Platform Runner subscription or a login on a paired machine. */
export const SubscriptionConnection = Schema.Struct({
  /** `platform`: a Platform Runner subscription; `local`: a CLI login on a paired machine. */
  kind: Schema.Literals(["platform", "local"]),
  /** Decision 0166 §3.6: the connection's own key (core `subscriptionKey`: `<runnerId>:<id>`). */
  key: Schema.String,
  /** The subscription id (Platform) or the profile id on its machine (local). */
  id: Schema.String,
  runnerId: Schema.String,
  /** The machine's name (local); null for the Platform Runner. */
  runnerName: Schema.NullOr(Schema.String),
  /** The owner's name for this connection; the account's name is the newest of these. */
  label: Schema.NullOr(Schema.String),
  /** The account as this connection knows it (Platform: unmasked; local: the runner's masked label). */
  account: Schema.NullOr(Schema.String),
  accountMasked: Schema.Boolean,
  /** Its own sharing, as enforcement reads it (the account's setting is written to each connection). */
  scope: ProfileScope,
  projectIds: Schema.Array(Schema.String),
  lastUsedAt: Schema.NullOr(Schema.Number),
  /** The machine (or the Platform Runner) is connected now; when it was last seen (null: unknown). */
  online: Schema.Boolean,
  lastSeenAt: Schema.NullOr(Schema.Number),
})
export type SubscriptionConnection = typeof SubscriptionConnection.Type

/** Decision 0167 §2: one plan limit window from the freshest observation among the account's connections. */
export const SubscriptionLimit = Schema.Struct({
  ...QuotaWindow.fields,
  observedAt: Schema.NullOr(Schema.Number),
})
export type SubscriptionLimit = typeof SubscriptionLimit.Type

const UsageSum = Schema.Struct({ runs: Schema.Number, listMicroUsd: Schema.Number })

/**
 * Decision 0167 §2: what an account paid in this workspace this month, at API prices (micro-USD), split by project (the
 * viewer's projects; others are left out) and by whose work it was (`askedBy`: "user:<id>", "agent:<id>", "unknown").
 */
export const AccountUsage = Schema.Struct({
  runs: Schema.Number,
  listMicroUsd: Schema.Number,
  projects: Schema.Array(Schema.Struct({ projectId: Schema.String, ...UsageSum.fields })),
  askers: Schema.Array(Schema.Struct({ askedBy: Schema.String, ...UsageSum.fields })),
})
export type AccountUsage = typeof AccountUsage.Type

export const SubscriptionAccount = Schema.Struct({
  /**
   * `acct:<ref>` for an account with a known account key; a connection without one is its own subscription and keeps
   * its connection key. Also its key in a person's choice per project (§6).
   */
  key: Schema.String,
  /** True when connections are grouped by an account key (sharing is then set once for all of them). */
  grouped: Schema.Boolean,
  ownerUserId: Schema.String,
  agent: Schema.String,
  plan: Schema.NullOr(Schema.String),
  /** The owner's name for it (the newest connection label); null = the plan, or the provider. */
  label: Schema.NullOr(Schema.String),
  /** The account email: unmasked from a connection that knows it (Platform first), else a masked label. */
  account: Schema.NullOr(Schema.String),
  accountMasked: Schema.Boolean,
  /** The account's sharing (§4). */
  scope: ProfileScope,
  projectIds: Schema.Array(Schema.String),
  lastUsedAt: Schema.NullOr(Schema.Number),
  connections: Schema.Array(SubscriptionConnection),
  limits: Schema.Array(SubscriptionLimit),
  /** The latest run on this account reported extra usage. */
  extraUsage: Schema.Boolean,
  usage: AccountUsage,
  /** Decision 0167 §4: for the owner only, the connections whose sharing differed when it was merged. */
  sharingNote: Schema.NullOr(Schema.Struct({ connections: Schema.Array(Schema.String), at: Schema.Number })),
})
export type SubscriptionAccount = typeof SubscriptionAccount.Type

export const SubscriptionsOverview = Schema.Struct({
  accounts: Schema.Array(SubscriptionAccount),
  /** Decision 0167 §2: runs this month on a person's plans whose connection is gone ("Earlier connections"). */
  earlier: Schema.Array(Schema.Struct({ ownerUserId: Schema.String, ...UsageSum.fields })),
  /** The projects the viewer sees, to name a subscription's projects (others count as private projects). */
  projects: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String })),
  /** The projects the viewer may share their own subscriptions in (they are a member or an admin). */
  shareable: Schema.Array(Schema.String),
  /** This month so far: what each person's plans and keys ran, at list prices (micro-USD), from the usage ledger. */
  costs: Schema.Array(
    Schema.Struct({
      ownerUserId: Schema.String,
      listMicroUsd: Schema.Number,
      runs: Schema.Number,
    }),
  ),
  from: Schema.String,
  to: Schema.String,
  /**
   * Decision 0166 §3.6: the viewer's choice per project. Decision 0167 §6: account keys (`acct:<ref>`), and the
   * connection key of a connection without an account. A project without an entry: nothing chosen, everything they
   * may use.
   */
  chosen: Schema.Record(Schema.String, Schema.Array(Schema.String)),
})
export type SubscriptionsOverview = typeof SubscriptionsOverview.Type

/** Decision 0167 §4: the body of `PUT /api/workspaces/:ws/subscriptions/accounts/:key/sharing` (owner only). */
export const AccountSharingBody = Schema.Struct({
  scope: ProfileScope,
  projectIds: Schema.optionalKey(Schema.Array(Schema.String).check(Schema.isMaxLength(200))),
})

/** Decision 0167 §3: one of the viewer's accounts in one workspace. */
export const MySubscriptionWorkspace = Schema.Struct({
  workspaceId: Schema.String,
  workspaceName: Schema.String,
  /** The account's key there (the same `acct:<ref>` in every workspace), for the link to its card. */
  key: Schema.String,
  scope: ProfileScope,
  /** The names of the projects it is shared in (the viewer is in every workspace listed). */
  projectNames: Schema.Array(Schema.String),
  /** Where it is connected there: "Platform Runner", machine names. */
  connections: Schema.Array(Schema.String),
  runs: Schema.Number,
  listMicroUsd: Schema.Number,
})
export type MySubscriptionWorkspace = typeof MySubscriptionWorkspace.Type

export const MySubscription = Schema.Struct({
  key: Schema.String,
  agent: Schema.String,
  plan: Schema.NullOr(Schema.String),
  label: Schema.NullOr(Schema.String),
  account: Schema.NullOr(Schema.String),
  accountMasked: Schema.Boolean,
  /** Account-wide: the freshest observation from any workspace. */
  limits: Schema.Array(SubscriptionLimit),
  extraUsage: Schema.Boolean,
  workspaces: Schema.Array(MySubscriptionWorkspace),
  /** This month in every workspace together. */
  runs: Schema.Number,
  listMicroUsd: Schema.Number,
})
export type MySubscription = typeof MySubscription.Type

/** Decision 0167 §3: `GET /api/me/subscriptions`: only the viewer's own accounts, from every workspace they are in. */
export const MySubscriptionsView = Schema.Struct({
  accounts: Schema.Array(MySubscription),
  from: Schema.String,
  to: Schema.String,
})
export type MySubscriptionsView = typeof MySubscriptionsView.Type
